/**
 * Minha conta (`api/v2/account/overview` e `api/v2/account/profile`): pagamentos com as notas
 * fiscais e o perfil do negócio. Sem a rota no ar, a página mostra só a licença e avisa.
 */
import { api } from '../lib/apiClient';
import type { ApiResult } from '../types/api';

export interface AccountPurchase {
  id: string;
  date: string | null;
  status: 'paid' | 'refunded';
  refundedAt: string | null;
  method: string | null;
  /** Em reais. */
  amount: number | null;
  currency: string;
  installments: number | null;
  products: string[];
  bonus: number;
  /** Período do código de ativação da compra, em dias. */
  days: number | null;
  invoice: {
    pdfUrl: string;
    xmlUrl: string | null;
    issuedAt: string | null;
    canceled: boolean;
  } | null;
}

export interface AccountProfile {
  /** Chave antiga do setor (`crm.industry`). */
  industry: string | null;
  /** Chave antiga do tipo de cliente (`crm.customer`): companies, professionals, people, both. */
  customer: string | null;
  segment: string | null;
  reach: string | null;
  place: { city: string | null; region: string | null; country: string | null } | null;
}

export interface AccountOverview {
  purchases: AccountPurchase[];
  profile: AccountProfile;
}

export const accountOverviewKey = (userId: string) => ['account-overview', userId] as const;

export function fetchAccountOverview(): Promise<ApiResult<AccountOverview>> {
  return api.get<AccountOverview>('api/v2/account/overview');
}

export function saveAccountProfile(profile: {
  industry?: string;
  customer?: string;
  segment?: string;
  reach?: string;
}): Promise<ApiResult<{ profile: AccountProfile }>> {
  return api.put<{ profile: AccountProfile }>('api/v2/account/profile', profile);
}

/**
 * Renovação automática do checkout novo (`api/v2/sales/subscriptions`, decisão 354): as
 * assinaturas da conta (pelo email e pela marca) e o cancelamento. Cancelar para as cobranças
 * seguintes; o período já pago continua valendo.
 */
export interface SaleSubscription {
  id: string;
  /** pending (primeiro pagamento), active, past_due (cobrança vencida), canceled, refused, expired, failed. */
  status: string;
  brand: string;
  /** card ou pix_automatic. */
  method: string | null;
  /** Ciclo da renovação, em meses: 1, 3, 6 ou 12. */
  months: number | null;
  currency: string;
  /** Em centavos. */
  total: number;
  items: { offer: string; amount: number }[];
  lastPaidAt: string | null;
  canceledAt: string | null;
}

export const subscriptionsKey = (userId: string) => ['sales-subscriptions', userId] as const;

export function fetchSubscriptions(): Promise<ApiResult<{ subscriptions: SaleSubscription[] }>> {
  return api.get<{ subscriptions: SaleSubscription[] }>('api/v2/sales/subscriptions');
}

export function cancelSubscription(
  id: string,
): Promise<ApiResult<{ subscription: SaleSubscription }>> {
  return api.post<{ subscription: SaleSubscription }>(
    `api/v2/sales/subscriptions/${encodeURIComponent(id)}/cancel`,
  );
}

/**
 * Upgrade pelo painel (`api/v2/sales/upgrade`, decisões 383 e 394): quanto custa subir para o
 * Avançado agora, e por quê. O valor sai do servidor: a diferença entre o Avançado e o plano atual
 * no período da licença, vezes os dias que faltam. Em centavos.
 */
export type UpgradeReason =
  | 'brand'
  | 'trial'
  | 'already'
  | 'plan'
  | 'expired'
  | 'lifetime'
  | 'auto_renew'
  | 'period'
  | 'unavailable'
  | 'too_late';

export interface UpgradeQuote {
  eligible: boolean;
  reason: UpgradeReason | null;
  /** Plano atual: base ou classic. */
  plan?: 'base' | 'classic';
  currency?: string;
  gateway?: 'asaas' | 'stripe';
  /** Formas de pagar: pix e card (Asaas) ou card (Stripe). */
  methods?: string[];
  /** Período da licença, em meses. */
  months?: number;
  periodDays?: number;
  remainingDays?: number;
  expiredAt?: string;
  from?: { offer: string; name: string | null; amount: number };
  to?: { offer: string; name: string | null; product: string | null; amount: number };
  difference?: number;
  amount?: number;
  minimum?: number;
}

export interface UpgradeOrder {
  id: string;
  kind: string;
  /** open, awaiting_payment, paid, expired, failed. */
  status: string;
  currency: string;
  total: number;
  paidAt: string | null;
}

export interface UpgradeCharge {
  order: UpgradeOrder;
  token: string;
  quote: UpgradeQuote;
  method: 'pix' | 'card';
  /** Pix: QR Code (imagem em base64), copia e cola e prazo. */
  pix?: { payload: string; image: string | null; expiresAt: string };
  /** Cartão: a página de pagamento (fatura do Asaas ou Checkout do Stripe). */
  url?: string;
}

export const upgradeKey = (userId: string) => ['sales-upgrade', userId] as const;

export function fetchUpgrade(): Promise<ApiResult<UpgradeQuote>> {
  return api.get<UpgradeQuote>('api/v2/sales/upgrade');
}

export function startUpgrade(input: {
  method: 'pix' | 'card';
  tax?: { document: string };
  returnUrl?: string;
}): Promise<ApiResult<UpgradeCharge>> {
  return api.post<UpgradeCharge>('api/v2/sales/upgrade', input);
}

export function fetchUpgradeOrder(
  id: string,
): Promise<ApiResult<{ order: UpgradeOrder; applied: boolean }>> {
  return api.get<{ order: UpgradeOrder; applied: boolean }>(
    `api/v2/sales/upgrade/${encodeURIComponent(id)}`,
  );
}
